import classnames from 'classnames';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import { formatPaymentDate } from 'src/components/LeadAgentAcceptanceBanner/LeadAgentAcceptanceBanner.functions';
import { useLeadAgentTrialCapActions } from 'src/components/LeadAgentTrialCapBanner/LeadAgentTrialCapActions.hook';
import {
  resolveMeterPct,
  resolveRecommendedTierOffer,
  resolveTrialCapBannerView,
  sortTrialCapOptions,
} from 'src/components/LeadAgentTrialCapBanner/LeadAgentTrialCapBanner.functions';
import { formatPrice } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.functions';
import { useLeadAgentAcceptanceContext } from 'src/providers/LeadAgentAcceptanceProvider/LeadAgentAcceptance.context';
import type { tAlertDetailRendererProps } from './AlertDetail.registry';
import AlertDetailBody from './AlertDetailBody.component';
import AlertOfferLine from './AlertOfferLine.component';

const NS = 'common/leadAgentTrialCapBanner';

/**
 * Rich body for the trial-cap alert (U4/RET-1878): the consumption meter, the
 * recommended-tier offer and the option list, adopted from
 * `LeadAgentTrialCapBanner` (same resolvers and flows, RET-1633 copy
 * authority). The options run the REAL unlock/accept flows through
 * `useLeadAgentTrialCapActions` — the exact wiring the banner container uses,
 * extracted so both surfaces share one copy.
 *
 * Role scoping mirrors the banner: only the owner sees meter, offer and
 * options; the admin reads the informative sentence alone (the modal's body).
 *
 * COMPACT PASS (2026-08-28). The body was the big banner's, moved verbatim
 * into a 448px dialog, and it showed: title, body, gauge, a three-sentence
 * note, a four-line offer card, three options and the footer's own CTA +
 * acknowledgment. Five buttons, one of them the same "Desbloquear ahora"
 * printed twice. What the JTBD needs is what is left — what happened, how
 * much room is gone, what it costs to fix, and the one move that fixes it:
 *
 *  - the note keeps ONLY the deferral consequence. Its first half ("tus leads
 *    no están recibiendo respuesta") is the alert's own body now, and the
 *    dialog was saying it twice;
 *  - the offer collapses to plan + price (`AlertOfferLine`);
 *  - `decline` drops out of the action row into its own lighter line: it is
 *    not a peer of the button that brings the agent back;
 *  - the footer's CTA and acknowledgment are suppressed upstream
 *    (`ALERT_DETAIL_OWNS_ACTIONS`).
 */
export default function LeadAgentTrialCapDetail(
  props: tAlertDetailRendererProps,
) {
  const { t } = useTranslation(NS);
  const { t: tAlerts } = useTranslation(ALERT_CENTER_NS);
  const {
    isTrialSightingForOwner,
    isTrialSightingForAdmin,
    isTrialCappedForOwner,
    isTrialCappedForAdmin,
    isTrialExtendedCappedForOwner,
    isTrialExtendedCappedForAdmin,
    trialCapOffer,
    usageCapacityPct,
  } = useLeadAgentAcceptanceContext();
  const {
    onOptionClick,
    isNonMonthly,
    isNestedModalOpen,
    actionModals,
  } = useLeadAgentTrialCapActions();

  // While the unlock/accept modal is open, the host detail must not close
  // (REL-2 by composition). Cleanup releases the lock if this renderer
  // unmounts for its own reasons (live resolution swapping the body).
  const { onNestedModalChange } = props;
  useEffect(() => {
    onNestedModalChange(isNestedModalOpen);
    return () => onNestedModalChange(false);
  }, [isNestedModalOpen, onNestedModalChange]);

  const view = resolveTrialCapBannerView({
    isTrialSightingForOwner,
    isTrialSightingForAdmin,
    isTrialCappedForOwner,
    isTrialCappedForAdmin,
    isTrialExtendedCappedForOwner,
    isTrialExtendedCappedForAdmin,
  });

  // RET-2329 (KTD4) — the alert can outlive the provider's view flags for a
  // beat mid-refetch (or mid-refresh, RET-2329). This used to be an early
  // `return <AlertDetailBody …/>` here, a DIFFERENT root element than the
  // `<div className="AlertDetail">` below — React remounts the whole subtree
  // on a root-type change, which unmounted `{actionModals}` (the open
  // unlock/accept checkout) and aborted its in-flight preview. There is now a
  // SINGLE return with ONE root: the view-dependent pieces (meter, reco,
  // options) degrade to `null` in place rather than removing the element,
  // and `{actionModals}` stays the last child regardless of `view`.
  const variant = view?.variant ?? null;
  const role = view?.role ?? null;
  const copyScope = view ? `${variant}.${role}` : null;
  const isOwner = role === 'owner';
  const isPrototypeVariant = variant === 'sighting' || variant === 'capped';

  const trialEndShort = formatPaymentDate(trialCapOffer?.trialEnd ?? null);
  // `capped` pins the meter to 100 and never waits on the usage fetch — the
  // server's offer phase is the authority (legacy banner rule).
  const meterPct = isPrototypeVariant
    ? resolveMeterPct(variant === 'capped' ? 100 : usageCapacityPct)
    : null;
  const showMeterBar = isOwner && isPrototypeVariant && meterPct !== null;
  const showMeterNote = isOwner && isPrototypeVariant && trialEndShort !== null;

  const recommendedTierOffer = isPrototypeVariant
    ? resolveRecommendedTierOffer(trialCapOffer)
    : null;
  const showReco = isOwner && recommendedTierOffer !== null;
  const priceKey = isNonMonthly ? 'annual' : 'monthly';

  /*
   * EVERY option the backend offers, unfiltered.
   *
   * The last-days window used to strip `unlock_now` here, on §18's line that
   * "vender el desbloqueo por 2-3 días es mal negocio". `MLA-77` says otherwise:
   * the unlock charges the same first bridge the deferred path pays at trial
   * end, and paying it IS accepting the tier — so unlocking late costs nothing
   * extra and brings the agent back for the days that remain. With no price
   * penalty there was nothing to protect anyone from, and hiding a door the
   * offer carries was us choosing for the customer (Fanny's ruling,
   * 2026-09-01). Both doors stay open; the deadline rides in the copy instead.
   */
  const options = sortTrialCapOptions(trialCapOffer?.options ?? []);
  const showOptionCtas = isOwner && isPrototypeVariant;
  const offered = options;
  // `decline` leaves the action row. It is the only option that ends the
  // relationship, and next to two purchase buttons of the same size it read
  // as a third way to pay.
  const actionOptions = offered.filter((option) => option !== 'decline');
  const declineOption = offered.find((option) => option === 'decline') ?? null;

  return (
    <div className="AlertDetail" data-testid="alert-detail-la-trial-cap">
      <AlertDetailBody alert={props.alert} />

      {/* The reading and its consequence, spaced as one group and NOT boxed
          — the prototype's `.usage`. A filled panel here is what made this
          dialog read heavier than the banner it came from. */}
      {showMeterBar ? (
        <div
          className="AlertDetail__Meter"
          data-testid="alert-detail-trial-cap-meter"
        >
          <div className="AlertDetail__MeterMeta">
            <span
              className={classnames('AlertDetail__MeterLabel', {
                'AlertDetail__MeterLabel--capped': meterPct >= 100,
              })}
            >
              {t(`${copyScope}.meter.label`)}
            </span>
            <span
              className={classnames('AlertDetail__MeterValue', {
                'AlertDetail__MeterValue--capped': meterPct >= 100,
              })}
              data-testid="alert-detail-trial-cap-meter-value"
            >
              {t(`${copyScope}.meter.value`, { pct: meterPct })}
            </span>
          </div>
          <div
            className="AlertDetail__MeterBar"
            role="progressbar"
            aria-valuenow={meterPct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t(`${copyScope}.meter.label`)}
          >
            <div
              className={classnames('AlertDetail__MeterFill', {
                // Red once the trial cap actually stopped the agent. The
                // `capped` variant pins the meter to 100 regardless of the
                // usage fetch, so this follows the same authority.
                'AlertDetail__MeterFill--capped': meterPct >= 100,
              })}
              // Accepted inline-style exception: dynamic fill width.
              style={{ width: `${meterPct}%` }}
            />
          </div>

          {/* Only what deferring COSTS. The rest of the legacy note repeated
              the alert body one line above it. */}
          {showMeterNote ? (
            <p
              className="AlertDetail__Note"
              data-testid="alert-detail-trial-cap-note"
            >
              {/* The date is the load-bearing word, so it is bold — the
                  prototype's `<b>` inside `.usage-note`. */}
              <Trans
                t={tAlerts}
                i18nKey={`detail.leadAgentTrialCap.${variant}.note`}
                values={{ trialEnd: trialEndShort ?? '' }}
                components={{ b: <b /> }}
              />
            </p>
          ) : null}
        </div>
      ) : null}

      {showReco && recommendedTierOffer ? (
        <AlertOfferLine
          testId="alert-detail-trial-cap-reco"
          label={t(`${copyScope}.reco.label`)}
          plan={t(`${copyScope}.reco.plan`, {
            tier: t(`tier.${recommendedTierOffer.tier}`),
          })}
          price={t(`${copyScope}.reco.price.${priceKey}`, {
            amount: formatPrice({
              price: recommendedTierOffer.unlockAmountCents,
              currency: recommendedTierOffer.currency,
            }),
            listAmount: formatPrice({
              price: recommendedTierOffer.listAmountCents,
              currency: recommendedTierOffer.currency,
            }),
          })}
        />
      ) : null}

      {showOptionCtas ? (
        <div className="AlertDetail__Actions">
          {actionOptions.map((option) => (
            <button
              key={option}
              type="button"
              className={classnames('AlertDetail__OptionCta', {
                'AlertDetail__OptionCta--primary': option === 'unlock_now',
              })}
              onClick={() => onOptionClick(option)}
              data-testid={`alert-detail-trial-cap-option-${option}`}
            >
              {t(`${variant}.owner.options.${option}`)}
            </button>
          ))}

          {/* The way out shares the row, as the prototype's `.actions` does.
              Still the lightest thing in it — a link among two buttons. */}
          {declineOption ? (
            <button
              type="button"
              className="AlertDetail__OptionCta AlertDetail__OptionCta--link"
              onClick={() => {
                onOptionClick(declineOption);
                // R12 — decline writes no state; in a modal context "no,
                // gracias" reads as dismissal, so the detail closes with it.
                props.onRequestClose();
              }}
              data-testid={`alert-detail-trial-cap-option-${declineOption}`}
            >
              {t(`${variant}.owner.options.${declineOption}`)}
            </button>
          ) : null}
        </div>
      ) : null}

      {/*
        NO support button here anymore. `extendedCapped`'s alert CTA is now
        `intercom`, so the modal's footer carries "Contactar a soporte" and
        painting a second one in the body would rebuild the duplication this
        variant was just fixed for.
        The footer's route is also the better one: it goes through
        `useAlertCta`, which uses the app's Intercom hook and emits the
        center's CTA-click event, where this button poked `window.Intercom`
        directly and reported nothing.
      */}

      {actionModals}
    </div>
  );
}
